import React, { useState } from 'react';
import { Box, TextField, Typography, Autocomplete } from '@mui/material';

/**
 * COUNTRY_PHONE_CODES — all ITU-T assigned calling codes (~240 countries & territories).
 * Fields:
 *   code — ISO 3166-1 alpha-2 country code (unique key)
 *   name — display name
 *   dial — ITU-T calling code (digits only, no +)
 *   flag — emoji flag
 *   min  — minimum LOCAL subscriber digits (after the country code, no trunk prefix)
 *   max  — maximum LOCAL subscriber digits
 */
export const COUNTRY_PHONE_CODES = [
    // ── Zone 1: NANP (North American Numbering Plan) — all use +1, 10 local digits ──
    { code: 'US', name: 'United States',            dial: '1',   flag: '🇺🇸', min: 10, max: 10 },
    { code: 'CA', name: 'Canada',                   dial: '1',   flag: '🇨🇦', min: 10, max: 10 },
    { code: 'AI', name: 'Anguilla',                 dial: '1',   flag: '🇦🇮', min: 10, max: 10 },
    { code: 'AG', name: 'Antigua and Barbuda',      dial: '1',   flag: '🇦🇬', min: 10, max: 10 },
    { code: 'AS', name: 'American Samoa',           dial: '1',   flag: '🇦🇸', min: 10, max: 10 },
    { code: 'BS', name: 'Bahamas',                  dial: '1',   flag: '🇧🇸', min: 10, max: 10 },
    { code: 'BB', name: 'Barbados',                 dial: '1',   flag: '🇧🇧', min: 10, max: 10 },
    { code: 'BM', name: 'Bermuda',                  dial: '1',   flag: '🇧🇲', min: 10, max: 10 },
    { code: 'VG', name: 'British Virgin Islands',   dial: '1',   flag: '🇻🇬', min: 10, max: 10 },
    { code: 'KY', name: 'Cayman Islands',           dial: '1',   flag: '🇰🇾', min: 10, max: 10 },
    { code: 'DM', name: 'Dominica',                 dial: '1',   flag: '🇩🇲', min: 10, max: 10 },
    { code: 'DO', name: 'Dominican Republic',       dial: '1',   flag: '🇩🇴', min: 10, max: 10 },
    { code: 'GD', name: 'Grenada',                  dial: '1',   flag: '🇬🇩', min: 10, max: 10 },
    { code: 'GU', name: 'Guam',                     dial: '1',   flag: '🇬🇺', min: 10, max: 10 },
    { code: 'JM', name: 'Jamaica',                  dial: '1',   flag: '🇯🇲', min: 10, max: 10 },
    { code: 'MS', name: 'Montserrat',               dial: '1',   flag: '🇲🇸', min: 10, max: 10 },
    { code: 'MP', name: 'N. Mariana Islands',       dial: '1',   flag: '🇲🇵', min: 10, max: 10 },
    { code: 'PR', name: 'Puerto Rico',              dial: '1',   flag: '🇵🇷', min: 10, max: 10 },
    { code: 'KN', name: 'Saint Kitts and Nevis',    dial: '1',   flag: '🇰🇳', min: 10, max: 10 },
    { code: 'LC', name: 'Saint Lucia',              dial: '1',   flag: '🇱🇨', min: 10, max: 10 },
    { code: 'VC', name: 'St. Vincent & Grenadines', dial: '1',   flag: '🇻🇨', min: 10, max: 10 },
    { code: 'SX', name: 'Sint Maarten',             dial: '1',   flag: '🇸🇽', min: 10, max: 10 },
    { code: 'TC', name: 'Turks and Caicos',         dial: '1',   flag: '🇹🇨', min: 10, max: 10 },
    { code: 'TT', name: 'Trinidad and Tobago',      dial: '1',   flag: '🇹🇹', min: 10, max: 10 },
    { code: 'VI', name: 'US Virgin Islands',        dial: '1',   flag: '🇻🇮', min: 10, max: 10 },

    // ── Zone 2: Africa ──
    { code: 'EG', name: 'Egypt',                    dial: '20',  flag: '🇪🇬', min: 10, max: 10 },
    { code: 'ZA', name: 'South Africa',             dial: '27',  flag: '🇿🇦', min: 9,  max: 9  },
    { code: 'GR', name: 'Greece',                   dial: '30',  flag: '🇬🇷', min: 10, max: 10 },
    { code: 'MA', name: 'Morocco',                  dial: '212', flag: '🇲🇦', min: 9,  max: 9  },
    { code: 'DZ', name: 'Algeria',                  dial: '213', flag: '🇩🇿', min: 9,  max: 9  },
    { code: 'TN', name: 'Tunisia',                  dial: '216', flag: '🇹🇳', min: 8,  max: 8  },
    { code: 'LY', name: 'Libya',                    dial: '218', flag: '🇱🇾', min: 9,  max: 9  },
    { code: 'GM', name: 'Gambia',                   dial: '220', flag: '🇬🇲', min: 7,  max: 7  },
    { code: 'SN', name: 'Senegal',                  dial: '221', flag: '🇸🇳', min: 9,  max: 9  },
    { code: 'MR', name: 'Mauritania',               dial: '222', flag: '🇲🇷', min: 8,  max: 8  },
    { code: 'ML', name: 'Mali',                     dial: '223', flag: '🇲🇱', min: 8,  max: 8  },
    { code: 'GN', name: 'Guinea',                   dial: '224', flag: '🇬🇳', min: 9,  max: 9  },
    { code: 'CI', name: "Côte d'Ivoire",            dial: '225', flag: '🇨🇮', min: 10, max: 10 },
    { code: 'BF', name: 'Burkina Faso',             dial: '226', flag: '🇧🇫', min: 8,  max: 8  },
    { code: 'NE', name: 'Niger',                    dial: '227', flag: '🇳🇪', min: 8,  max: 8  },
    { code: 'TG', name: 'Togo',                     dial: '228', flag: '🇹🇬', min: 8,  max: 8  },
    { code: 'BJ', name: 'Benin',                    dial: '229', flag: '🇧🇯', min: 8,  max: 8  },
    { code: 'MU', name: 'Mauritius',                dial: '230', flag: '🇲🇺', min: 7,  max: 8  },
    { code: 'LR', name: 'Liberia',                  dial: '231', flag: '🇱🇷', min: 8,  max: 9  },
    { code: 'SL', name: 'Sierra Leone',             dial: '232', flag: '🇸🇱', min: 8,  max: 8  },
    { code: 'GH', name: 'Ghana',                    dial: '233', flag: '🇬🇭', min: 9,  max: 9  },
    { code: 'NG', name: 'Nigeria',                  dial: '234', flag: '🇳🇬', min: 10, max: 10 },
    { code: 'TD', name: 'Chad',                     dial: '235', flag: '🇹🇩', min: 8,  max: 8  },
    { code: 'CF', name: 'Central African Rep.',     dial: '236', flag: '🇨🇫', min: 8,  max: 8  },
    { code: 'CM', name: 'Cameroon',                 dial: '237', flag: '🇨🇲', min: 9,  max: 9  },
    { code: 'CV', name: 'Cape Verde',               dial: '238', flag: '🇨🇻', min: 7,  max: 7  },
    { code: 'ST', name: 'São Tomé & Príncipe',      dial: '239', flag: '🇸🇹', min: 7,  max: 7  },
    { code: 'GQ', name: 'Equatorial Guinea',        dial: '240', flag: '🇬🇶', min: 9,  max: 9  },
    { code: 'GA', name: 'Gabon',                    dial: '241', flag: '🇬🇦', min: 7,  max: 8  },
    { code: 'CG', name: 'Congo (Republic)',         dial: '242', flag: '🇨🇬', min: 9,  max: 9  },
    { code: 'CD', name: 'Congo (DRC)',              dial: '243', flag: '🇨🇩', min: 9,  max: 9  },
    { code: 'AO', name: 'Angola',                   dial: '244', flag: '🇦🇴', min: 9,  max: 9  },
    { code: 'GW', name: 'Guinea-Bissau',            dial: '245', flag: '🇬🇼', min: 7,  max: 7  },
    { code: 'IO', name: 'British Indian Ocean Ter.',dial: '246', flag: '🇮🇴', min: 7,  max: 7  },
    { code: 'AC', name: 'Ascension Island',         dial: '247', flag: '🇸🇭', min: 4,  max: 5  },
    { code: 'SC', name: 'Seychelles',               dial: '248', flag: '🇸🇨', min: 7,  max: 7  },
    { code: 'SD', name: 'Sudan',                    dial: '249', flag: '🇸🇩', min: 9,  max: 9  },
    { code: 'RW', name: 'Rwanda',                   dial: '250', flag: '🇷🇼', min: 9,  max: 9  },
    { code: 'ET', name: 'Ethiopia',                 dial: '251', flag: '🇪🇹', min: 9,  max: 9  },
    { code: 'SO', name: 'Somalia',                  dial: '252', flag: '🇸🇴', min: 8,  max: 9  },
    { code: 'DJ', name: 'Djibouti',                 dial: '253', flag: '🇩🇯', min: 8,  max: 8  },
    { code: 'KE', name: 'Kenya',                    dial: '254', flag: '🇰🇪', min: 9,  max: 9  },
    { code: 'TZ', name: 'Tanzania',                 dial: '255', flag: '🇹🇿', min: 9,  max: 9  },
    { code: 'UG', name: 'Uganda',                   dial: '256', flag: '🇺🇬', min: 9,  max: 9  },
    { code: 'BI', name: 'Burundi',                  dial: '257', flag: '🇧🇮', min: 8,  max: 8  },
    { code: 'MZ', name: 'Mozambique',               dial: '258', flag: '🇲🇿', min: 9,  max: 9  },
    { code: 'ZM', name: 'Zambia',                   dial: '260', flag: '🇿🇲', min: 9,  max: 9  },
    { code: 'MG', name: 'Madagascar',               dial: '261', flag: '🇲🇬', min: 9,  max: 9  },
    { code: 'RE', name: 'Réunion',                  dial: '262', flag: '🇷🇪', min: 9,  max: 9  },
    { code: 'ZW', name: 'Zimbabwe',                 dial: '263', flag: '🇿🇼', min: 9,  max: 9  },
    { code: 'NA', name: 'Namibia',                  dial: '264', flag: '🇳🇦', min: 9,  max: 9  },
    { code: 'MW', name: 'Malawi',                   dial: '265', flag: '🇲🇼', min: 9,  max: 9  },
    { code: 'LS', name: 'Lesotho',                  dial: '266', flag: '🇱🇸', min: 8,  max: 8  },
    { code: 'BW', name: 'Botswana',                 dial: '267', flag: '🇧🇼', min: 7,  max: 8  },
    { code: 'SZ', name: 'Eswatini',                 dial: '268', flag: '🇸🇿', min: 8,  max: 8  },
    { code: 'KM', name: 'Comoros',                  dial: '269', flag: '🇰🇲', min: 7,  max: 7  },
    { code: 'SH', name: 'Saint Helena',             dial: '290', flag: '🇸🇭', min: 4,  max: 5  },
    { code: 'ER', name: 'Eritrea',                  dial: '291', flag: '🇪🇷', min: 7,  max: 7  },
    { code: 'AW', name: 'Aruba',                    dial: '297', flag: '🇦🇼', min: 7,  max: 7  },
    { code: 'FO', name: 'Faroe Islands',            dial: '298', flag: '🇫🇴', min: 6,  max: 6  },
    { code: 'GL', name: 'Greenland',                dial: '299', flag: '🇬🇱', min: 6,  max: 6  },

    // ── Zone 3: Europe ──
    { code: 'NL', name: 'Netherlands',              dial: '31',  flag: '🇳🇱', min: 9,  max: 9  },
    { code: 'BE', name: 'Belgium',                  dial: '32',  flag: '🇧🇪', min: 9,  max: 9  },
    { code: 'FR', name: 'France',                   dial: '33',  flag: '🇫🇷', min: 9,  max: 9  },
    { code: 'ES', name: 'Spain',                    dial: '34',  flag: '🇪🇸', min: 9,  max: 9  },
    { code: 'HU', name: 'Hungary',                  dial: '36',  flag: '🇭🇺', min: 9,  max: 9  },
    { code: 'IT', name: 'Italy',                    dial: '39',  flag: '🇮🇹', min: 9,  max: 10 },
    { code: 'RO', name: 'Romania',                  dial: '40',  flag: '🇷🇴', min: 9,  max: 9  },
    { code: 'CH', name: 'Switzerland',              dial: '41',  flag: '🇨🇭', min: 9,  max: 9  },
    { code: 'AT', name: 'Austria',                  dial: '43',  flag: '🇦🇹', min: 10, max: 13 },
    { code: 'GB', name: 'United Kingdom',           dial: '44',  flag: '🇬🇧', min: 10, max: 10 },
    { code: 'DK', name: 'Denmark',                  dial: '45',  flag: '🇩🇰', min: 8,  max: 8  },
    { code: 'SE', name: 'Sweden',                   dial: '46',  flag: '🇸🇪', min: 9,  max: 9  },
    { code: 'NO', name: 'Norway',                   dial: '47',  flag: '🇳🇴', min: 8,  max: 8  },
    { code: 'PL', name: 'Poland',                   dial: '48',  flag: '🇵🇱', min: 9,  max: 9  },
    { code: 'DE', name: 'Germany',                  dial: '49',  flag: '🇩🇪', min: 10, max: 12 },
    { code: 'GI', name: 'Gibraltar',                dial: '350', flag: '🇬🇮', min: 8,  max: 8  },
    { code: 'PT', name: 'Portugal',                 dial: '351', flag: '🇵🇹', min: 9,  max: 9  },
    { code: 'LU', name: 'Luxembourg',               dial: '352', flag: '🇱🇺', min: 9,  max: 9  },
    { code: 'IE', name: 'Ireland',                  dial: '353', flag: '🇮🇪', min: 9,  max: 9  },
    { code: 'IS', name: 'Iceland',                  dial: '354', flag: '🇮🇸', min: 7,  max: 7  },
    { code: 'AL', name: 'Albania',                  dial: '355', flag: '🇦🇱', min: 9,  max: 9  },
    { code: 'MT', name: 'Malta',                    dial: '356', flag: '🇲🇹', min: 8,  max: 8  },
    { code: 'CY', name: 'Cyprus',                   dial: '357', flag: '🇨🇾', min: 8,  max: 8  },
    { code: 'FI', name: 'Finland',                  dial: '358', flag: '🇫🇮', min: 9,  max: 10 },
    { code: 'BG', name: 'Bulgaria',                 dial: '359', flag: '🇧🇬', min: 9,  max: 9  },
    { code: 'LT', name: 'Lithuania',                dial: '370', flag: '🇱🇹', min: 8,  max: 8  },
    { code: 'LV', name: 'Latvia',                   dial: '371', flag: '🇱🇻', min: 8,  max: 8  },
    { code: 'EE', name: 'Estonia',                  dial: '372', flag: '🇪🇪', min: 7,  max: 8  },
    { code: 'MD', name: 'Moldova',                  dial: '373', flag: '🇲🇩', min: 8,  max: 8  },
    { code: 'AM', name: 'Armenia',                  dial: '374', flag: '🇦🇲', min: 8,  max: 8  },
    { code: 'BY', name: 'Belarus',                  dial: '375', flag: '🇧🇾', min: 9,  max: 9  },
    { code: 'AD', name: 'Andorra',                  dial: '376', flag: '🇦🇩', min: 6,  max: 9  },
    { code: 'MC', name: 'Monaco',                   dial: '377', flag: '🇲🇨', min: 8,  max: 9  },
    { code: 'SM', name: 'San Marino',               dial: '378', flag: '🇸🇲', min: 10, max: 10 },
    { code: 'UA', name: 'Ukraine',                  dial: '380', flag: '🇺🇦', min: 9,  max: 9  },
    { code: 'RS', name: 'Serbia',                   dial: '381', flag: '🇷🇸', min: 9,  max: 9  },
    { code: 'ME', name: 'Montenegro',               dial: '382', flag: '🇲🇪', min: 8,  max: 8  },
    { code: 'XK', name: 'Kosovo',                   dial: '383', flag: '🇽🇰', min: 8,  max: 8  },
    { code: 'HR', name: 'Croatia',                  dial: '385', flag: '🇭🇷', min: 9,  max: 9  },
    { code: 'SI', name: 'Slovenia',                 dial: '386', flag: '🇸🇮', min: 8,  max: 8  },
    { code: 'BA', name: 'Bosnia and Herzegovina',   dial: '387', flag: '🇧🇦', min: 8,  max: 8  },
    { code: 'MK', name: 'North Macedonia',          dial: '389', flag: '🇲🇰', min: 8,  max: 8  },
    { code: 'CZ', name: 'Czech Republic',           dial: '420', flag: '🇨🇿', min: 9,  max: 9  },
    { code: 'SK', name: 'Slovakia',                 dial: '421', flag: '🇸🇰', min: 9,  max: 9  },
    { code: 'LI', name: 'Liechtenstein',            dial: '423', flag: '🇱🇮', min: 7,  max: 9  },

    // ── Zone 5: Central & South America ──
    { code: 'FK', name: 'Falkland Islands',         dial: '500', flag: '🇫🇰', min: 5,  max: 5  },
    { code: 'BZ', name: 'Belize',                   dial: '501', flag: '🇧🇿', min: 7,  max: 7  },
    { code: 'GT', name: 'Guatemala',                dial: '502', flag: '🇬🇹', min: 8,  max: 8  },
    { code: 'SV', name: 'El Salvador',              dial: '503', flag: '🇸🇻', min: 8,  max: 8  },
    { code: 'HN', name: 'Honduras',                 dial: '504', flag: '🇭🇳', min: 8,  max: 8  },
    { code: 'NI', name: 'Nicaragua',                dial: '505', flag: '🇳🇮', min: 8,  max: 8  },
    { code: 'CR', name: 'Costa Rica',               dial: '506', flag: '🇨🇷', min: 8,  max: 8  },
    { code: 'PA', name: 'Panama',                   dial: '507', flag: '🇵🇦', min: 7,  max: 8  },
    { code: 'PM', name: 'Saint Pierre & Miquelon',  dial: '508', flag: '🇵🇲', min: 6,  max: 6  },
    { code: 'HT', name: 'Haiti',                    dial: '509', flag: '🇭🇹', min: 8,  max: 8  },
    { code: 'PE', name: 'Peru',                     dial: '51',  flag: '🇵🇪', min: 9,  max: 9  },
    { code: 'MX', name: 'Mexico',                   dial: '52',  flag: '🇲🇽', min: 10, max: 10 },
    { code: 'CU', name: 'Cuba',                     dial: '53',  flag: '🇨🇺', min: 8,  max: 8  },
    { code: 'AR', name: 'Argentina',                dial: '54',  flag: '🇦🇷', min: 10, max: 10 },
    { code: 'BR', name: 'Brazil',                   dial: '55',  flag: '🇧🇷', min: 11, max: 11 },
    { code: 'CL', name: 'Chile',                    dial: '56',  flag: '🇨🇱', min: 9,  max: 9  },
    { code: 'CO', name: 'Colombia',                 dial: '57',  flag: '🇨🇴', min: 10, max: 10 },
    { code: 'VE', name: 'Venezuela',                dial: '58',  flag: '🇻🇪', min: 10, max: 10 },
    { code: 'GP', name: 'Guadeloupe',               dial: '590', flag: '🇬🇵', min: 9,  max: 9  },
    { code: 'BO', name: 'Bolivia',                  dial: '591', flag: '🇧🇴', min: 8,  max: 8  },
    { code: 'GY', name: 'Guyana',                   dial: '592', flag: '🇬🇾', min: 7,  max: 7  },
    { code: 'EC', name: 'Ecuador',                  dial: '593', flag: '🇪🇨', min: 9,  max: 9  },
    { code: 'GF', name: 'French Guiana',            dial: '594', flag: '🇬🇫', min: 9,  max: 9  },
    { code: 'PY', name: 'Paraguay',                 dial: '595', flag: '🇵🇾', min: 9,  max: 9  },
    { code: 'MQ', name: 'Martinique',               dial: '596', flag: '🇲🇶', min: 9,  max: 9  },
    { code: 'SR', name: 'Suriname',                 dial: '597', flag: '🇸🇷', min: 7,  max: 7  },
    { code: 'UY', name: 'Uruguay',                  dial: '598', flag: '🇺🇾', min: 9,  max: 9  },
    { code: 'CW', name: 'Curaçao',                  dial: '599', flag: '🇨🇼', min: 7,  max: 8  },

    // ── Zone 6: Asia-Pacific ──
    { code: 'MY', name: 'Malaysia',                 dial: '60',  flag: '🇲🇾', min: 9,  max: 10 },
    { code: 'AU', name: 'Australia',                dial: '61',  flag: '🇦🇺', min: 9,  max: 9  },
    { code: 'ID', name: 'Indonesia',                dial: '62',  flag: '🇮🇩', min: 9,  max: 12 },
    { code: 'PH', name: 'Philippines',              dial: '63',  flag: '🇵🇭', min: 10, max: 10 },
    { code: 'NZ', name: 'New Zealand',              dial: '64',  flag: '🇳🇿', min: 8,  max: 10 },
    { code: 'SG', name: 'Singapore',                dial: '65',  flag: '🇸🇬', min: 8,  max: 8  },
    { code: 'TH', name: 'Thailand',                 dial: '66',  flag: '🇹🇭', min: 9,  max: 9  },
    { code: 'TL', name: 'Timor-Leste',              dial: '670', flag: '🇹🇱', min: 7,  max: 8  },
    { code: 'NF', name: 'Norfolk Island',           dial: '672', flag: '🇳🇫', min: 6,  max: 6  },
    { code: 'BN', name: 'Brunei',                   dial: '673', flag: '🇧🇳', min: 7,  max: 7  },
    { code: 'NR', name: 'Nauru',                    dial: '674', flag: '🇳🇷', min: 7,  max: 7  },
    { code: 'PG', name: 'Papua New Guinea',         dial: '675', flag: '🇵🇬', min: 7,  max: 8  },
    { code: 'TO', name: 'Tonga',                    dial: '676', flag: '🇹🇴', min: 5,  max: 7  },
    { code: 'SB', name: 'Solomon Islands',          dial: '677', flag: '🇸🇧', min: 5,  max: 7  },
    { code: 'VU', name: 'Vanuatu',                  dial: '678', flag: '🇻🇺', min: 7,  max: 7  },
    { code: 'FJ', name: 'Fiji',                     dial: '679', flag: '🇫🇯', min: 7,  max: 7  },
    { code: 'PW', name: 'Palau',                    dial: '680', flag: '🇵🇼', min: 7,  max: 7  },
    { code: 'WF', name: 'Wallis and Futuna',        dial: '681', flag: '🇼🇫', min: 6,  max: 6  },
    { code: 'CK', name: 'Cook Islands',             dial: '682', flag: '🇨🇰', min: 5,  max: 5  },
    { code: 'NU', name: 'Niue',                     dial: '683', flag: '🇳🇺', min: 4,  max: 4  },
    { code: 'WS', name: 'Samoa',                    dial: '685', flag: '🇼🇸', min: 5,  max: 7  },
    { code: 'KI', name: 'Kiribati',                 dial: '686', flag: '🇰🇮', min: 8,  max: 8  },
    { code: 'NC', name: 'New Caledonia',            dial: '687', flag: '🇳🇨', min: 6,  max: 6  },
    { code: 'TV', name: 'Tuvalu',                   dial: '688', flag: '🇹🇻', min: 5,  max: 6  },
    { code: 'PF', name: 'French Polynesia',         dial: '689', flag: '🇵🇫', min: 6,  max: 8  },
    { code: 'TK', name: 'Tokelau',                  dial: '690', flag: '🇹🇰', min: 4,  max: 4  },
    { code: 'FM', name: 'Micronesia',               dial: '691', flag: '🇫🇲', min: 7,  max: 7  },
    { code: 'MH', name: 'Marshall Islands',         dial: '692', flag: '🇲🇭', min: 7,  max: 7  },

    // ── Zone 7: Russia & Kazakhstan ──
    { code: 'RU', name: 'Russia',                   dial: '7',   flag: '🇷🇺', min: 10, max: 10 },
    { code: 'KZ', name: 'Kazakhstan',               dial: '77',  flag: '🇰🇿', min: 10, max: 10 },

    // ── Zone 8: East Asia ──
    { code: 'JP', name: 'Japan',                    dial: '81',  flag: '🇯🇵', min: 10, max: 11 },
    { code: 'KR', name: 'South Korea',              dial: '82',  flag: '🇰🇷', min: 9,  max: 11 },
    { code: 'VN', name: 'Vietnam',                  dial: '84',  flag: '🇻🇳', min: 9,  max: 10 },
    { code: 'KP', name: 'North Korea',              dial: '850', flag: '🇰🇵', min: 8,  max: 10 },
    { code: 'HK', name: 'Hong Kong',                dial: '852', flag: '🇭🇰', min: 8,  max: 8  },
    { code: 'MO', name: 'Macau',                    dial: '853', flag: '🇲🇴', min: 8,  max: 8  },
    { code: 'KH', name: 'Cambodia',                 dial: '855', flag: '🇰🇭', min: 8,  max: 9  },
    { code: 'LA', name: 'Laos',                     dial: '856', flag: '🇱🇦', min: 8,  max: 9  },
    { code: 'CN', name: 'China',                    dial: '86',  flag: '🇨🇳', min: 11, max: 11 },
    { code: 'BD', name: 'Bangladesh',               dial: '880', flag: '🇧🇩', min: 10, max: 10 },
    { code: 'TW', name: 'Taiwan',                   dial: '886', flag: '🇹🇼', min: 9,  max: 9  },

    // ── Zone 9: South & Southeast Asia, Middle East ──
    { code: 'TR', name: 'Turkey',                   dial: '90',  flag: '🇹🇷', min: 10, max: 10 },
    { code: 'IN', name: 'India',                    dial: '91',  flag: '🇮🇳', min: 10, max: 10 },
    { code: 'PK', name: 'Pakistan',                 dial: '92',  flag: '🇵🇰', min: 10, max: 10 },
    { code: 'AF', name: 'Afghanistan',              dial: '93',  flag: '🇦🇫', min: 9,  max: 9  },
    { code: 'LK', name: 'Sri Lanka',                dial: '94',  flag: '🇱🇰', min: 9,  max: 9  },
    { code: 'MM', name: 'Myanmar',                  dial: '95',  flag: '🇲🇲', min: 8,  max: 10 },
    { code: 'MV', name: 'Maldives',                 dial: '960', flag: '🇲🇻', min: 7,  max: 7  },
    { code: 'LB', name: 'Lebanon',                  dial: '961', flag: '🇱🇧', min: 7,  max: 8  },
    { code: 'JO', name: 'Jordan',                   dial: '962', flag: '🇯🇴', min: 9,  max: 9  },
    { code: 'SY', name: 'Syria',                    dial: '963', flag: '🇸🇾', min: 9,  max: 9  },
    { code: 'IQ', name: 'Iraq',                     dial: '964', flag: '🇮🇶', min: 10, max: 10 },
    { code: 'KW', name: 'Kuwait',                   dial: '965', flag: '🇰🇼', min: 8,  max: 8  },
    { code: 'SA', name: 'Saudi Arabia',             dial: '966', flag: '🇸🇦', min: 9,  max: 9  },
    { code: 'YE', name: 'Yemen',                    dial: '967', flag: '🇾🇪', min: 9,  max: 9  },
    { code: 'OM', name: 'Oman',                     dial: '968', flag: '🇴🇲', min: 8,  max: 8  },
    { code: 'PS', name: 'Palestinian Territories',  dial: '970', flag: '🇵🇸', min: 9,  max: 9  },
    { code: 'AE', name: 'UAE',                      dial: '971', flag: '🇦🇪', min: 9,  max: 9  },
    { code: 'IL', name: 'Israel',                   dial: '972', flag: '🇮🇱', min: 9,  max: 9  },
    { code: 'BH', name: 'Bahrain',                  dial: '973', flag: '🇧🇭', min: 8,  max: 8  },
    { code: 'QA', name: 'Qatar',                    dial: '974', flag: '🇶🇦', min: 8,  max: 8  },
    { code: 'BT', name: 'Bhutan',                   dial: '975', flag: '🇧🇹', min: 8,  max: 8  },
    { code: 'MN', name: 'Mongolia',                 dial: '976', flag: '🇲🇳', min: 8,  max: 8  },
    { code: 'NP', name: 'Nepal',                    dial: '977', flag: '🇳🇵', min: 10, max: 10 },
    { code: 'IR', name: 'Iran',                     dial: '98',  flag: '🇮🇷', min: 10, max: 10 },
    { code: 'TJ', name: 'Tajikistan',               dial: '992', flag: '🇹🇯', min: 9,  max: 9  },
    { code: 'TM', name: 'Turkmenistan',             dial: '993', flag: '🇹🇲', min: 8,  max: 8  },
    { code: 'AZ', name: 'Azerbaijan',               dial: '994', flag: '🇦🇿', min: 9,  max: 9  },
    { code: 'GE', name: 'Georgia',                  dial: '995', flag: '🇬🇪', min: 9,  max: 9  },
    { code: 'KG', name: 'Kyrgyzstan',               dial: '996', flag: '🇰🇬', min: 9,  max: 9  },
    { code: 'UZ', name: 'Uzbekistan',               dial: '998', flag: '🇺🇿', min: 9,  max: 9  },
].sort((a, b) => a.name.localeCompare(b.name));

/**
 * PhoneInputField — searchable country code dropdown + local number input.
 *
 * Props:
 *   value        — full international number, e.g. "+263772345679"
 *   countryCode  — ISO code of selected country, e.g. "ZW"  (stored in parent state)
 *   onChange(fullNumber, dialCode, countryCode)
 *   error        — boolean
 *   helperText   — error string (shown when error=true)
 *   required     — boolean
 *   label        — label for the local number input
 *   size         — MUI size prop
 */
export default function PhoneInputField({
    value = '',
    countryCode = '',
    onChange,
    error = false,
    helperText = '',
    required = false,
    label = 'WhatsApp / Phone Number',
    size = 'small',
}) {
    const DEFAULT_CODE = 'ZW'; // Change to your primary market if needed

    const getInitialCountry = () =>
        COUNTRY_PHONE_CODES.find(c => c.code === (countryCode || DEFAULT_CODE))
        || COUNTRY_PHONE_CODES.find(c => c.code === DEFAULT_CODE);

    const [selectedCountry, setSelectedCountry] = useState(getInitialCountry);

    const getInitialLocal = () => {
        if (!value || !selectedCountry) return '';
        return value.replace(/\D/g, '').slice(selectedCountry.dial.length);
    };

    const [localNumber, setLocalNumber] = useState(getInitialLocal);

    const handleCountryChange = (_, newCountry) => {
        if (!newCountry) return;
        setSelectedCountry(newCountry);
        setLocalNumber('');
        onChange('', newCountry.dial, newCountry.code);
    };

    const handleLocalChange = (e) => {
        const digits = e.target.value.replace(/\D/g, '');
        if (selectedCountry && digits.length > selectedCountry.max) return;
        setLocalNumber(digits);
        const full = digits ? `+${selectedCountry.dial}${digits}` : '';
        onChange(full, selectedCountry.dial, selectedCountry.code);
    };

    const hint = (() => {
        if (error && helperText) return { text: helperText, isError: true };
        if (selectedCountry) {
            const range = selectedCountry.min === selectedCountry.max
                ? `${selectedCountry.min}`
                : `${selectedCountry.min}–${selectedCountry.max}`;
            return {
                text: `${selectedCountry.name}: ${range} digits required after +${selectedCountry.dial}`,
                isError: false,
            };
        }
        return null;
    })();

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>

                {/* ── Searchable country code selector ── */}
                <Autocomplete
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    options={COUNTRY_PHONE_CODES}
                    // What appears in the input field after selection (compact)
                    getOptionLabel={(option) => `${option.flag} +${option.dial}`}
                    // Search by name, dial code, or ISO code
                    filterOptions={(options, { inputValue }) => {
                        const q = inputValue.trim().toLowerCase().replace(/^\+/, '');
                        if (!q) return options;
                        return options.filter(o =>
                            o.name.toLowerCase().includes(q) ||
                            o.dial.startsWith(q) ||
                            o.code.toLowerCase().startsWith(q)
                        );
                    }}
                    isOptionEqualToValue={(option, val) => option.code === val.code}
                    disableClearable
                    autoHighlight
                    blurOnSelect
                    openOnFocus
                    ListboxProps={{ style: { maxHeight: 270 } }}
                    sx={{ width: 158, flexShrink: 0 }}
                    renderOption={(props, option) => {
                        const { key, ...rest } = props;
                        return (
                            <Box component="li" key={key} {...rest}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '6px !important' }}
                            >
                                <span style={{ fontSize: '1.1em', lineHeight: 1, flexShrink: 0 }}>
                                    {option.flag}
                                </span>
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', minWidth: 36, fontFamily: 'monospace', flexShrink: 0 }}
                                >
                                    +{option.dial}
                                </Typography>
                                <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {option.name}
                                </Typography>
                            </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Country"
                            size={size}
                            error={error}
                            placeholder="Search…"
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                '& .MuiInputLabel-outlined.Mui-focused': { color: '#259c30' },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#259c30',
                                },
                            }}
                        />
                    )}
                />

                {/* ── Local number digits ── */}
                <TextField
                    fullWidth
                    required={required}
                    label={label}
                    value={localNumber}
                    onChange={handleLocalChange}
                    error={error}
                    size={size}
                    inputProps={{
                        inputMode: 'numeric',
                        maxLength: selectedCountry ? selectedCountry.max : 15,
                    }}
                    placeholder={selectedCountry ? `${'0'.repeat(Math.min(selectedCountry.min, 9))}` : 'Enter number'}
                    sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                        '& .MuiInputLabel-outlined.Mui-focused': { color: '#259c30' },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#259c30',
                        },
                    }}
                />
            </Box>

            {/* Unified hint / error line below both inputs */}
            {hint && (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mt: 0.5,
                        ml: '14px',
                        color: hint.isError ? 'error.main' : 'text.secondary',
                        lineHeight: 1.4,
                    }}
                >
                    {hint.text}
                </Typography>
            )}
        </Box>
    );
}
